import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { JobCard } from '../components/Jobs/JobCard';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { JobFilters } from '../components/Jobs/Filters/JobFilters';
import { JobForm } from '../components/Listings/Forms/JobForm';
import { Hero } from '../components/Hero';
import { supabase } from '../lib/supabase';
import { europeanCountries } from '../lib/constants/countries';
import { getCurrentLocation, filterByDistance, type LocationCoords } from '../lib/utils/locationFilter';
import type { Job } from '../types/jobs';

interface Filters {
  jobTypes: string[];
  countries: string[];
}

export function Jobs() {
  const PAGE_SIZE = 12;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<(Job & { distance?: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userLocation, setUserLocation] = useState<LocationCoords | null>(null);
  const [filters, setFilters] = useState<Filters>({
    jobTypes: [],
    countries: []
  });
  const userHasInteracted = useRef(false);
  const searchQueryParam = searchParams.get('q')?.trim() || '';
  const locationParamRaw = searchParams.get('location')?.trim() || '';
  const locationKeyword = locationParamRaw.split(',')[0]?.trim() || locationParamRaw;

  // Initialize filters from URL parameters
  useEffect(() => {
    const countryParam = searchParams.get('country');
    
    if (countryParam) {
      setFilters(prev => ({
        ...prev,
        countries: [countryParam]
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(0);
    setJobs([]);
  }, [filters, searchParams, refreshKey]);

  useEffect(() => {
    async function fetchJobs() {
      try {
        if (page === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        // Only use URL parameter if user hasn't interacted with filters yet
        const countryParam = searchParams.get('country');
        const effectiveFilters = (countryParam && !userHasInteracted.current)
          ? { ...filters, countries: [countryParam] }
          : filters;
          
        let query = supabase
          .from('job_offers')
          .select(`
            *,
            user:users (
              id,
              username,
              full_name,
              avatar_url,
              user_type,
              subscription_status,
              manual_professional
            )
          `)
          .eq('approval_status', 'approved')
          .eq('user.user_type', 'professional');

        if (effectiveFilters.jobTypes.length > 0) {
          query = query.in('job_type', effectiveFilters.jobTypes);
        }

        if (effectiveFilters.countries.length > 0) {
          const expandedCountries = Array.from(new Set(
            effectiveFilters.countries.flatMap((c) => {
              const match = europeanCountries.find(ec => ec.value === c || ec.label === c);
              return match ? [match.value, match.label] : [c];
            })
          ));
          query = query.in('country', expandedCountries);
        }
        if (locationKeyword) {
          query = query.or(`location.ilike.%${locationKeyword}%,country.ilike.%${locationKeyword}%`);
        }

        query = query
          .order('created_at', { ascending: false })
          .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

        const { data, error } = await query;
        if (error) throw error;

        const rows = data || [];
        setHasMore(rows.length === PAGE_SIZE);
        setJobs(prev => page === 0 ? rows : [...prev, ...rows]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        if (page === 0) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }

    fetchJobs();
  }, [filters, searchParams, refreshKey, page]);

  // Apply URL and location filters client-side
  useEffect(() => {
    let filtered = [...jobs];

    if (searchQueryParam) {
      const query = searchQueryParam.toLowerCase();
      filtered = filtered.filter(job =>
        job.title?.toLowerCase().includes(query) ||
        job.company_name?.toLowerCase().includes(query) ||
        job.description?.toLowerCase().includes(query) ||
        job.job_type?.toLowerCase().includes(query)
      );
    }

    if (locationKeyword) {
      const keyword = locationKeyword.toLowerCase();
      filtered = filtered.filter(job =>
        job.location?.toLowerCase().includes(keyword) ||
        job.country?.toLowerCase().includes(keyword)
      );
    }

    if (userLocation) {
      filtered = filterByDistance(filtered, userLocation, 100);
    }

    setFilteredJobs(filtered);
  }, [jobs, userLocation, searchQueryParam, locationKeyword]);

  const handleFilterChange = (filterType: keyof Filters, values: string[]) => {
    // Mark that user has interacted with filters
    userHasInteracted.current = true;
    
    setFilters(prev => ({
      ...prev,
      [filterType]: values
    }));
  };

  const handleAddressSelect = (latitude: number, longitude: number, address: string) => {
    setUserLocation({ latitude, longitude });
  };

  const handleCreateListing = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else {
      setShowCreateModal(true);
    }
  };

  return (
    <div>
      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/59bed50f-5ccf-4265-87fa-7743af34d361/wellness-practitioner-jobs.png"
          alt="Wellness Career Opportunities"
          className="w-full h-full object-cover object-top shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('jobs.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('jobs.subtitle')}
        </p>
        <button 
          onClick={handleCreateListing}
          className="inline-flex items-center px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors shadow-sm whitespace-nowrap"
        >
          {t('jobs.submitListing')}
        </button>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block">
        <Hero
          title={t('jobs.title')}
          subtitle={t('jobs.subtitle')}
          image="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/59bed50f-5ccf-4265-87fa-7743af34d361/wellness-practitioner-jobs.png"
          showAddListing
          onAddListing={handleCreateListing}
          addListingButtonText={t('jobs.submitListing')}
        />
      </div>

      <div className="px-0 sm:px-6 lg:px-10 mt-16 lg:mt-8">
        <JobFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onAddressSelect={handleAddressSelect}
          showExpanded={true}
        />
        <div className="max-w-page mx-auto">

        <div className="px-4 sm:px-0 mt-6">
        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {loading ? (
          <SkeletonGrid count={6} />
        ) : filteredJobs.length === 0 ? (
          <div className="bg-background rounded-lg p-8 text-center">
            <p className="text-content">
              {userLocation ? t('jobs.noJobsNearby') : t('jobs.noJobsFilter')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-0">
              {filteredJobs.map((job) => (
                <JobCard key={job.id} job={job} distance={(job as any).distance} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setPage(prev => prev + 1)}
                  disabled={loadingMore}
                  className="px-6 py-2.5 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors text-sm disabled:opacity-60"
                >
                  {loadingMore ? 'Loading...' : 'Show more'}
                </button>
              </div>
            )}
          </>
        )}
        </div>
        </div>
      </div>

      {showCreateModal && (
        <JobForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}